import Axios from 'axios';
import { User } from './userApi';
import { Branch } from './branchApi';
import { Client } from './clientsApi';
import { ImportMode } from './rateImportApi';
import { postRateImportFile as postRateImportFileShared, downloadRateTemplate, sanitizeFilenamePart } from './rateImportApi';

export interface EquipmentCategory {
  id?: string;
  equipmentItems?: EquipmentItem[];
  name: string;
  description?: string;
  totalStock?: number;
  numCheckedOut?: number;
  isDeleted?: boolean;
  isCapping?: boolean;
}

export interface EquipmentItem {
  id?: string;
  categoryId: string;
  referenceId?: string;
  model?: string;
  make?: string;
  equipmentHistoryLogs?: EquipmentItemHistory[];
  location?: string;
  state?: string;
  country?: string;
  tagId?: string;
  tagTestExpiryDate?: string;
  branchId?: string;
  branchName?: string;
}

export interface EquipmentItemGroup {
  categoryId: string;
  referenceIdList: string[];
  model?: string;
  make?: string;
}

export interface EquipmentItemHistory {
  id?: string;
  equipmentItem: EquipmentItem;
  userId: string;
  jobId: string;
  updatedOn: string;
  IsACheckOut: boolean;
  createdByUser: User;
  createdByUserId: string;
}

export type BuyAndSellRateType = {
  id: string;
  organizationId: number;
  equipmentCategoryId: string;
  branchId: string;
  branch: Branch;
  buyRate: string;
  sellRate: string;
  effectiveFrom: string;
  createdOn: string;
  updatedOn: string;
  client?: Client;
  clientId?: string;
  name?: string;
  isDefault: boolean;
};

export type EquipmentCategoryItem = {
  id: string;
  equipmentCategoryId?: string;
  name: string;
  tags?: string[];
  rank?: number;
  sellRates?: EquipmentBuyAndSellRateType[];
  buyRates?: EquipmentBuyAndSellRateType[];
  isDeleted?: boolean;
};

export type EquipmentRateFormType = {
  name: string;
  sellRate?: number;
  country?: string;
  state?: string;
  branchId?: string;
  effectiveFrom: string;
  clientId: string;
  equipmentCategoryId: string;
  buyRate?: number;
  isDefault: boolean;
  client: Client;
  branch: Branch;
  id: string;
};

export type EquipmentBuyAndSellRateType = {
  id: string;
  organizationId: number;
  equipmentItemId: string;
  branchId: string;
  branch: Branch;
  buyRate: string;
  sellRate: string;
  effectiveFrom: string;
  createdOn: string;
  updatedOn: string;
  client?: Client;
  clientId?: string;
  name?: string;
  isDefault: boolean;
  equipmentCategoryId?: string;
};

export async function createNewEquipmentCategory({
  data,
}: {
  data: EquipmentCategory;
}) {
  const res = await Axios.post(`/api/v1/equipment/category`, { ...data });
  return res.data;
}

export async function updateEquipmentCategory(
  id: string,
  data: Partial<EquipmentCategory>
) {
  const res = await Axios.put(`/api/v1/equipment/category/${id}`, data);
  return res.data;
}

export async function deleteEquipmentCategory(categoryId: string) {
  await Axios.delete(`/api/v1/equipment/${categoryId}`);
}

export async function createNewEquipmentItem({
  data,
}: {
  data: EquipmentItem;
}) {
  const res = await Axios.post(`/api/v1/equipment/item`, { ...data });
  return res.data;
}

export async function updateEquipmentItem(
  id: string,
  data: Partial<EquipmentItem>
) {
  const res = await Axios.put(`/api/v1/equipment/item/${id}`, data);
  return res.data;
}

export async function deleteEquipmentItem(categoryId: string, itemId: string) {
  await Axios.delete(`/api/v1/equipment/category/${categoryId}/item/${itemId}`);
}

export async function listCategoryData(): Promise<EquipmentCategory[]> {
  const res = await Axios.get('/api/v1/equipment/category-list');
  return res.data;
}

export async function getCategoryById(id: string): Promise<any> {
  const res = await Axios.get(`/api/v1/equipment/category/${id}`);
  return res.data;
}

export async function saveEquipmentByCSV({
  file,
  country,
  state,
  branchId,
}: {
  file: File;
  country: string;
  state: string;
  branchId?: string;
}): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('country', country);
  formData.append('state', state);
  formData.append('branchId', branchId || '');
  const res = await Axios.post(`/api/v1/equipment/import`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return res.data;
}

export async function addEquipmentBuyRate({ data }: { data }) {
  const res = await Axios.post(`/api/v1/equipment/buy-rate`, data);
  return res.data;
}

export async function addEquipmentSellRate({ data }: { data }) {
  const res = await Axios.post(`/api/v1/equipment/sell-rate`, data);
  return res.data;
}

export async function listEquipmentItems(): Promise<any[]> {
  const res = await Axios.get(`/api/v1/equipment/item-list`);
  return res.data;
}

export async function addEquipmentCategory({
  data,
}: {
  data: EquipmentCategoryItem;
}) {
  const res = await Axios.post(
    `/api/v1/equipment/equipment-category-item`,
    data
  );
  return res.data;
}

export async function deleteEquipmentRateItem(id: string) {
  try {
    const response = await Axios.delete(
      `/api/v1/equipment/equipment-rate-category-item/${id}`
    );
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete equipment item: ${error}`);
  }
}

export async function deleteEquipmentSellRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/equipment/sell-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete sell rate: ${error}`);
  }
}

export async function deleteEquipmentBuyRate(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/equipment/buy-rates/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete buy rate: ${error}`);
  }
}

export async function updateEquipmentCategoryItem({
  id,
  data,
}: {
  id: string | number;
  data: Partial<EquipmentCategoryItem>;
}) {
  const res = await Axios.put(`/api/v1/equipment/${id}`, data);
  return res.data;
}

export async function deleteEquipmentCategoryItem(id: string) {
  try {
    const response = await Axios.delete(
      `/api/v1/equipment/category-rate-item/${id}`
    );
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete equipment category item: ${error}`);
  }
}

export async function updateEquipmentItemRanks({
  data,
}: {
  data: { id: string; rank: number }[];
}) {
  const res = await Axios.put(`/api/v1/equipment/category-ranks`, data);
  return res.data;
}

export async function updateRate({
  id,
  data,
  rateType,
}: {
  id: string;
  data: Partial<EquipmentRateFormType>;
  rateType: 'buyRate' | 'sellRate';
}) {
  const res = await Axios.put(`/api/v1/equipment/${rateType}/${id}`, data);
  return res.data;
}

// Equipment Rate Bulk Import
// Equipment Categories already exist and can never be created through this import — every row is
// matched by the hidden Equipment Category ID, on New templates and Update templates alike.

export type EquipmentRateFieldPlan = {
  action: 'insert' | 'update' | 'unchanged';
  newValue: number;
  oldValue?: number;
  rateId?: string;
  effectiveFrom: string;
  dateStatus: 'past' | 'today' | 'future';
  dateMoved?: boolean;
};

export type EquipmentRateRowPlan = {
  row: number;
  equipmentCategoryId: string;
  name: string;
  buy?: EquipmentRateFieldPlan;
  sell?: EquipmentRateFieldPlan;
};

export type BulkUpdateEquipmentRatesResponse = {
  committed: boolean;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  ignoredCount: number;
  errorCount: number;
  created: EquipmentRateRowPlan[];
  updated: EquipmentRateRowPlan[];
  ignored: { row: number; name?: string; reason: string }[];
  errors: { row: number; name?: string; message: string }[];
  previewToken?: string;
};

function postEquipmentRateImportFile(
  url: string,
  file: File,
  extraFields?: Record<string, string>
): Promise<BulkUpdateEquipmentRatesResponse> {
  return postRateImportFileShared<BulkUpdateEquipmentRatesResponse>(url, file, extraFields);
}

export async function compareEquipmentDefaultRatesImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  return postEquipmentRateImportFile(
    '/api/v1/equipment/default-rates/import/compare/new',
    file
  );
}

export async function compareEquipmentDefaultRatesUpdateImport({
  file,
}: {
  file: File;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  return postEquipmentRateImportFile(
    '/api/v1/equipment/default-rates/import/compare/update',
    file
  );
}

export async function bulkUpdateEquipmentDefaultRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  const res = await Axios.post('/api/v1/equipment/default-rates/import', {
    previewToken,
  });
  return res.data;
}

// Buy-only template, scoped to one branch
export async function compareEquipmentBranchBuyRateImport({
  file,
  branchId,
}: {
  file: File;
  branchId: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  return postEquipmentRateImportFile(
    '/api/v1/equipment/rates/buy/import/compare/new',
    file,
    { branchId }
  );
}

export async function compareEquipmentBranchBuyRateUpdateImport({
  file,
  branchId,
}: {
  file: File;
  branchId: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  return postEquipmentRateImportFile(
    '/api/v1/equipment/rates/buy/import/compare/update',
    file,
    { branchId }
  );
}

// Sell-only template, scoped to a branch, a client, or both
export async function compareEquipmentBranchClientSellRateImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postEquipmentRateImportFile(
    '/api/v1/equipment/rates/sell/import/compare/new',
    file,
    extraFields
  );
}

export async function compareEquipmentBranchClientSellRateUpdateImport({
  file,
  branchId,
  clientId,
}: {
  file: File;
  branchId?: string;
  clientId?: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  const extraFields: Record<string, string> = {};
  if (branchId) extraFields.branchId = branchId;
  if (clientId) extraFields.clientId = clientId;
  return postEquipmentRateImportFile(
    '/api/v1/equipment/rates/sell/import/compare/update',
    file,
    extraFields
  );
}

// Reuses the previewed plan — shared commit endpoint for both buy and sell scoped imports
export async function bulkUpdateEquipmentScopedRates({
  previewToken,
}: {
  previewToken: string;
}): Promise<BulkUpdateEquipmentRatesResponse> {
  const res = await Axios.post('/api/v1/equipment/rates/import', {
    previewToken,
  });
  return res.data;
}

export async function downloadEquipmentDefaultRatesNewTemplate() {
  return downloadRateTemplate(
    '/api/v1/equipment/default-rates/import/template/new',
    'restorize_equipment_rates_default_new_template.xlsx'
  );
}

export async function downloadEquipmentDefaultRatesUpdateTemplate() {
  return downloadRateTemplate(
    '/api/v1/equipment/default-rates/import/template/update',
    'restorize_equipment_rates_default_update_template.xlsx'
  );
}

export async function hasExistingEquipmentDefaultRates(): Promise<boolean> {
  const res = await Axios.get('/api/v1/equipment/default-rates/has-existing');
  return res.data.exists;
}

export function getEquipmentBranchBuyRatesTemplateFilename(
  mode: ImportMode,
  branchName: string
): string {
  return `restorize_equipment_rates_buy_${mode}_${sanitizeFilenamePart(branchName)}_template.xlsx`;
}

export function getEquipmentSellRatesTemplateFilename(
  mode: ImportMode,
  branchName?: string,
  clientName?: string
): string {
  const scopeParts = [branchName, clientName].filter(Boolean).map(sanitizeFilenamePart);
  return `restorize_equipment_rates_sell_${mode}_${scopeParts.join('-')}_template.xlsx`;
}

export async function downloadEquipmentBranchBuyRatesNewTemplate({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}) {
  return downloadRateTemplate(
    '/api/v1/equipment/rates/buy/import/template/new',
    getEquipmentBranchBuyRatesTemplateFilename('new', branchName),
    { branchId }
  );
}

export async function downloadEquipmentBranchBuyRatesUpdateTemplate({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}) {
  return downloadRateTemplate(
    '/api/v1/equipment/rates/buy/import/template/update',
    getEquipmentBranchBuyRatesTemplateFilename('update', branchName),
    { branchId }
  );
}

export async function hasExistingEquipmentBranchBuyRates({
  branchId,
}: {
  branchId: string;
}): Promise<boolean> {
  const res = await Axios.get('/api/v1/equipment/rates/buy/has-existing', {
    params: { branchId },
  });
  return res.data.exists;
}

export async function downloadEquipmentSellRatesNewTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/equipment/rates/sell/import/template/new',
    getEquipmentSellRatesTemplateFilename('new', branchName, clientName),
    { branchId, clientId }
  );
}

export async function downloadEquipmentSellRatesUpdateTemplate({
  branchId,
  clientId,
  branchName,
  clientName,
}: {
  branchId?: string;
  clientId?: string;
  branchName?: string;
  clientName?: string;
}) {
  return downloadRateTemplate(
    '/api/v1/equipment/rates/sell/import/template/update',
    getEquipmentSellRatesTemplateFilename('update', branchName, clientName),
    { branchId, clientId }
  );
}

export async function hasExistingEquipmentSellRates({
  branchId,
  clientId,
}: {
  branchId?: string;
  clientId?: string;
}): Promise<boolean> {
  const res = await Axios.get('/api/v1/equipment/rates/sell/has-existing', {
    params: { branchId, clientId },
  });
  return res.data.exists;
}
